import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Award,
  ChevronUp,
  Zap,
  Tag
} from 'lucide-react';
import '../styles/wordpress-blog.css';
import { BlogPost, blogPosts } from '../content/blog/blogData';
import { fetchBlogPosts } from '../services/blogApi';
import GoogleAd from './GoogleAd';
import SeoMeta from './SeoMeta';
import OptimizedImage from './OptimizedImage';
import NotFoundPage from './NotFoundPage';

interface BlogPageProps {}

const BlogPage: React.FC<BlogPageProps> = () => {

  const { slug } = useParams<{ slug: string }>();
  // Published posts from the blog DB (daily pipeline, after owner approval)
  // are fetched client-side and listed ahead of the static posts.
  const [dbPosts, setDbPosts] = useState<BlogPost[]>([]);
  const [dbLoaded, setDbLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchBlogPosts()
      .then(posts => { if (!cancelled) setDbPosts(posts); })
      .catch(() => { /* fall back to static posts only */ })
      .finally(() => { if (!cancelled) setDbLoaded(true); });
    return () => { cancelled = true; };
  }, []);
  const allPosts: BlogPost[] = [...dbPosts, ...blogPosts];

  // Derived from the URL (not effect-driven state) so static articles also
  // render during build-time prerendering, where effects never run.
  const selectedPost: BlogPost | null = slug ? allPosts.find((p: BlogPost) => p.slug === slug) ?? null : null;
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showGoToTop, setShowGoToTop] = useState(false);


  const getAuthorAvatar = (author: { name: string; avatar?: string }) => {
    if (author.name === "Shantanu Bose") {
      return {
        src: "/avatars/shantanu-bose.jpg",
        fallback: "https://media.licdn.com/dms/image/v2/C4E03AQFTf2xU5Rcx6w/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1615278921047?e=1757548800&v=beta&t=H948tsBkP7FE_oPK7u9KdSD2mNlO1LwITuwO3Ko8oNA"
      };
    }
    return { src: author.avatar };
  };

  const extractHeadings = (htmlContent: string) => {
    const headings: { id: string; text: string; level: number }[] = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const headingElements = tempDiv.querySelectorAll('h2, h3');
    headingElements.forEach((heading) => {
      const text = heading.textContent || '';
      const level = parseInt(heading.tagName.substring(1));
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      headings.push({ id, text, level });
      heading.id = id;
    });
    return headings;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowGoToTop(scrollTop > 300);
    };

    if (selectedPost) {
      const timeoutId = setTimeout(() => {
        const contentElement = document.querySelector('.article-content');
        if (contentElement) {
          const headingElements = contentElement.querySelectorAll('h1, h2, h3, h4, h5, h6');
          headingElements.forEach((heading) => {
            const text = heading.textContent || '';
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9\s]/g, '')
              .replace(/\s+/g, '-')
              .trim();
            heading.id = id;
          });
        }
      }, 100);

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(timeoutId);
      };
    }
  }, [selectedPost]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Share buttons: native share sheet where available, else copy the link.
  const [shareNote, setShareNote] = useState('');
  const shareArticle = async () => {
    if (!selectedPost) return;
    const url = `https://indianstockmarketgpt.com/blog/${selectedPost.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: selectedPost.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareNote('Link copied');
    } catch {
      return; // user cancelled the share sheet or clipboard is unavailable
    }
    setTimeout(() => setShareNote(''), 2000);
  };

  const categories = ['All', 'Foundation Building', 'Financial Analysis', 'Risk Management', 'Investment Strategy', 'Sector Analysis', 'Portfolio Management', 'Advanced Strategies'];

  const filteredPosts: BlogPost[] = selectedCategory === 'All'
    ? allPosts
    : allPosts.filter((post: BlogPost) => post.category === selectedCategory);

  const featuredPost = allPosts.find((post: BlogPost) => post.featured);


  // Unknown article slug: once DB posts have loaded, render the 404 page
  // (noindex) instead of silently redirecting to /blog (a soft 404).
  if (slug && !selectedPost) {
    if (!dbLoaded) {
      return <div className="min-h-[60vh]" aria-busy="true" />;
    }
    return <NotFoundPage />;
  }

  return (
    <>
      {!selectedPost && (
        <SeoMeta
          title="Indian Stock Market Blog — AI Analysis, Investing Guides & Strategies"
          description="Free expert guides on Indian stock market investing — Nifty, Bank Nifty, SIP, mutual funds, technical analysis, and AI-powered stock research. Written for Indian retail investors."
          keywords="indian stock market blog, nifty analysis, bank nifty guide, SIP investing, mutual funds india, technical analysis india, AI stock market India, stock market for beginners india"
          url="https://indianstockmarketgpt.com/blog"
          type="website"
          siteName="IndianStockMarketGPT"
        />
      )}
      {selectedPost && (
        <SeoMeta
          title={selectedPost.metaTitle || `${selectedPost.title} | IndianStockMarketGPT`}
          description={selectedPost.metaDescription || selectedPost.excerpt}
          keywords={[...selectedPost.seoKeywords, ...selectedPost.tags].join(', ')}
          url={`https://indianstockmarketgpt.com/blog/${selectedPost.slug}`}
          image={selectedPost.image}
          type="article"
          siteName="IndianStockMarketGPT"
        >
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://indianstockmarketgpt.com/blog/${selectedPost.slug}` },
            "headline": selectedPost.metaTitle || selectedPost.title,
            "description": selectedPost.metaDescription || selectedPost.excerpt,
            "image": selectedPost.image,
            "author": { "@type": "Person", "name": selectedPost.author.name, "url": "https://indianstockmarketgpt.com/about" },
            "publisher": { "@type": "Organization", "name": "IndianStockMarketGPT", "logo": { "@type": "ImageObject", "url": "https://indianstockmarketgpt.com/favicon-192x192.svg" } },
            "datePublished": selectedPost.publishedAt,
            "dateModified": selectedPost.publishedAt,
            "keywords": selectedPost.seoKeywords.join(', '),
            "articleSection": selectedPost.category,
            "url": `https://indianstockmarketgpt.com/blog/${selectedPost.slug}`
          }) }} />
        </SeoMeta>
      )}

      {selectedPost ? (
        <div className="min-h-screen bg-white text-slate-900">
          {/* Sticky Header */}
          <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <Link
                  to="/blog"
                  className="flex items-center text-slate-500 hover:text-navy-500 transition-colors"
                  aria-label="Back to Blog"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
                  <span>Back to Blog</span>
                </Link>
                <div className="flex items-center space-x-4">
                  <div className="hidden md:flex items-center space-x-3">
                    <button onClick={shareArticle} aria-label="Share this article" className="text-slate-400 hover:text-navy-500 transition-colors">
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="text-sm text-slate-500">
                    {selectedPost.readTime} min read
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-12 gap-8">
              {/* Left Sidebar */}
              <div className="hidden md:block md:col-span-2">
                <div className="sticky top-24 space-y-6">
                  {/* Table of Contents */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-1 h-4 bg-navy-500 rounded-full"></div>
                      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        In This Article
                      </h3>
                    </div>
                    <nav className="space-y-1 max-h-80 overflow-y-auto">
                      {extractHeadings(selectedPost.content).length === 0 ? (
                        <p className="text-xs text-slate-500 italic py-2">No headings found</p>
                      ) : (
                        extractHeadings(selectedPost.content).map((heading, index) => (
                          <a
                            key={index}
                            href={`#${heading.id}`}
                            className={`group flex items-center space-x-2 py-1.5 px-2 rounded-md hover:bg-slate-100 transition-all duration-200 ${
                              heading.level === 2
                                ? 'text-slate-700 hover:text-navy-500'
                                : 'text-slate-500 hover:text-navy-500 ml-3'
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              const element = document.getElementById(heading.id);
                              if (element) {
                                window.scrollTo({ top: element.offsetTop - 100, behavior: 'smooth' });
                              }
                            }}
                          >
                            {heading.level === 2 ? (
                              <>
                                <div className="w-1.5 h-1.5 bg-navy-500 rounded-full flex-shrink-0"></div>
                                <span className="text-xs font-medium leading-tight group-hover:translate-x-0.5 transition-transform truncate">
                                  {heading.text}
                                </span>
                              </>
                            ) : (
                              <>
                                <div className="w-1 h-1 bg-slate-400 rounded-full flex-shrink-0"></div>
                                <span className="text-xs leading-tight group-hover:translate-x-0.5 transition-transform truncate">
                                  {heading.text}
                                </span>
                              </>
                            )}
                          </a>
                        ))
                      )}
                    </nav>
                    {(() => {
                      const headings = extractHeadings(selectedPost.content);
                      const h2Count = headings.filter(h => h.level === 2).length;
                      const h3Count = headings.filter(h => h.level === 3).length;
                      if (headings.length > 0) {
                        return (
                          <div className="mt-3 pt-2 border-t border-slate-200">
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span>{h2Count} sections</span>
                              {h3Count > 0 && <span>{h3Count} sub</span>}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Social Proof */}
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex -space-x-2">
                        <img src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop" className="w-6 h-6 rounded-full border-2 border-white" alt="Reader avatar" />
                        <img src="https://images.pexels.com/photos/3785079/pexels-photo-3785079.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop" className="w-6 h-6 rounded-full border-2 border-white" alt="Reader avatar" />
                        <img src="https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop" className="w-6 h-6 rounded-full border-2 border-white" alt="Reader avatar" />
                      </div>
                      <div className="text-xs text-slate-500">+2.5K readers</div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Join thousands of investors reading our insights daily
                    </p>
                  </div>
                </div>
              </div>

              {/* Main Article Content */}
              <div className="col-span-12 md:col-span-8">
                <article className="prose prose-slate prose-sm max-w-none">
                  <header className="mb-4">
                    <div className="mb-6">
                      <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6 bg-navy-50 text-navy-600 border border-navy-100">
                        {selectedPost.category}
                      </span>
                      <h1 className="text-xl md:text-2xl lg:text-3xl font-bold leading-tight mb-4 text-slate-900 font-serif">
                        {selectedPost.title}
                      </h1>
                    </div>

                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-4">
                        <img
                          src={getAuthorAvatar(selectedPost.author).src}
                          alt={selectedPost.author.name}
                          className="w-14 h-14 rounded-full ring-2 ring-slate-200"
                          width="56" height="56"
                          onError={e => {
                            const fallback = getAuthorAvatar(selectedPost.author).fallback;
                            if (fallback && e.currentTarget.src !== fallback) {
                              e.currentTarget.src = fallback;
                            }
                          }}
                        />
                        <div>
                          <div className="text-lg font-semibold text-slate-900">{selectedPost.author.name}</div>
                          <div className="text-sm text-slate-500">{selectedPost.author.bio}</div>
                          <div className="flex items-center space-x-4 text-sm text-slate-500 mt-1">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {new Date(selectedPost.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {selectedPost.readTime} min read
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center space-x-6">
                        <button onClick={shareArticle} aria-label="Share this article" className="text-slate-400 hover:text-navy-500 transition-colors group">
                          <Share2 className="w-6 h-6 group-hover:scale-110 transition-transform" />
                        </button>
                      </div>
                    </div>

                    {selectedPost.image && (
                      <div className="relative mb-12 group">
                        <OptimizedImage
                          src={selectedPost.image}
                          alt={selectedPost.title}
                          className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-500"
                          width={1200}
                          height={400}
                          priority={true}
                          aspectRatio={3}
                          fallbackSrc="/favicon.svg"
                        />
                      </div>
                    )}
                  </header>

                  {!selectedPost.noAds && (
                    <GoogleAd
                      adFormat="horizontal"
                      style={{ display: 'block', minHeight: '90px' }}
                      className="my-6 w-full"
                    />
                  )}

                  <div
                    className="article-content wordpress-style"
                    dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                  />

                  {/* Mandatory SEBI disclaimer */}
                  <div className="mt-8 p-4 rounded-xl border border-slate-200 bg-slate-50">
                    <p className="text-xs text-slate-500 leading-relaxed">
                      <strong className="text-slate-700">Educational content only.</strong>{' '}
                      This article is for informational and educational purposes only. It does not constitute investment advice, a recommendation to buy or sell any security, or a solicitation of any kind. IndianStockMarketGPT is not a SEBI-registered research analyst or investment adviser. Past performance of any stock, fund, or strategy is not indicative of future results. Always consult a SEBI-registered investment adviser before making financial decisions.
                    </p>
                  </div>

                  {!selectedPost.noAds && (
                    <GoogleAd
                      adFormat="horizontal"
                      style={{ display: 'block', minHeight: '90px' }}
                      className="my-6 w-full"
                    />
                  )}

                  {shareNote && (
                    <div role="status" className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">
                      {shareNote}
                    </div>
                  )}

                  {showGoToTop && (
                    <button
                      onClick={scrollToTop}
                      className="fixed bottom-8 right-8 bg-navy-500 hover:bg-navy-600 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-50"
                      aria-label="Go to top"
                    >
                      <ChevronUp className="w-6 h-6" />
                    </button>
                  )}

                  {/* Tags */}
                  <div className="mt-16 pt-8 border-t border-slate-100">
                    <div className="flex flex-wrap gap-3">
                      {selectedPost.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center bg-slate-100 text-slate-600 px-4 py-2 rounded-full text-sm border border-slate-200 hover:border-navy-200 hover:text-navy-600 transition-colors cursor-pointer"
                        >
                          <Tag className="w-3 h-3 mr-2" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Related Posts */}
                  {selectedPost.internalLinks && selectedPost.internalLinks.length > 0 && (() => {
                    const related = selectedPost.internalLinks
                      .map((linkSlug: string) => blogPosts.find((p: BlogPost) => p.slug.includes(linkSlug) || linkSlug.includes(p.id)))
                      .filter(Boolean) as BlogPost[];
                    return related.length > 0 ? (
                      <div className="mt-12 pt-8 border-t border-slate-100">
                        <h3 className="text-xl font-bold mb-5 text-slate-900 font-serif">Further Reading</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {related.map((rp: BlogPost) => (
                            <Link
                              key={rp.id}
                              to={`/blog/${rp.slug}`}
                              className="group flex items-start gap-3 bg-slate-50 hover:bg-white border border-slate-200 hover:border-navy-300 rounded-xl p-4 transition-all"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-800 group-hover:text-navy-500 transition-colors line-clamp-2">{rp.title}</p>
                                <p className="text-xs text-slate-500 mt-1">{rp.readTime} min read · {rp.category}</p>
                              </div>
                              <ArrowLeft className="w-4 h-4 text-slate-400 group-hover:text-navy-500 rotate-180 shrink-0 mt-1 transition-colors" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Affiliate CTA */}
                  <div className="mt-12 rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="bg-navy-50 border-b border-slate-200 px-6 py-3 flex items-center gap-2">
                      <span className="text-xs font-semibold text-navy-600 uppercase tracking-widest">Ready to invest?</span>
                      <span className="text-xs text-slate-600">Open a free demat account in 10 minutes</span>
                    </div>
                    <div className="bg-white px-6 py-6">
                      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                        <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                          <a href="https://zerodha.com/open-account?c=JA0171" target="_blank" rel="noopener noreferrer sponsored"
                            className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                            <span className="w-5 h-5 rounded bg-blue-600 flex items-center justify-center text-[10px] font-bold text-white">Z</span>
                            Zerodha
                          </a>
                          <a href="https://upstox.onelink.me/0H1s/KY1170" target="_blank" rel="noopener noreferrer sponsored"
                            className="flex items-center gap-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                            <span className="w-5 h-5 rounded bg-purple-600 flex items-center justify-center text-[10px] font-bold text-white">U</span>
                            Upstox
                          </a>
                          <a href="https://angel-one.onelink.me/Wjgr/ahr63txg" target="_blank" rel="noopener noreferrer sponsored"
                            className="flex items-center gap-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                            <span className="w-5 h-5 rounded bg-orange-600 flex items-center justify-center text-[10px] font-bold text-white">A</span>
                            Angel One
                          </a>
                        </div>
                        <Link to="/best-demat-account-india"
                          className="text-xs text-navy-500 hover:text-navy-700 underline underline-offset-2 whitespace-nowrap transition-colors">
                          Full comparison →
                        </Link>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-3">Affiliate disclosure: we earn a small commission if you open an account — at no cost to you.</p>
                    </div>
                  </div>

                  {/* Author Bio */}
                  <div className="mt-16 p-8 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="flex items-start space-x-6">
                      <img
                        src={getAuthorAvatar(selectedPost.author).src}
                        alt={selectedPost.author.name}
                        className="w-20 h-20 rounded-full ring-2 ring-slate-200"
                        width="80" height="80"
                        onError={e => {
                          const fallback = getAuthorAvatar(selectedPost.author).fallback;
                          if (fallback && e.currentTarget.src !== fallback) {
                            e.currentTarget.src = fallback;
                          }
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2 text-slate-900 font-serif">{selectedPost.author.name}</h3>
                        <p className="text-slate-600 mb-4 leading-relaxed text-sm">{selectedPost.author.bio}</p>
                        <div className="flex items-center space-x-4">
                          <a
                            href="https://www.linkedin.com/in/iamshantanubose/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-navy-500 hover:bg-navy-600 text-white px-6 py-2 rounded-lg transition-colors text-sm font-medium"
                          >
                            Follow
                          </a>
                          <button onClick={shareArticle} aria-label="Share this article" className="text-slate-400 hover:text-navy-500 transition-colors">
                            <Share2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>

              {/* Right Sidebar */}
              {!selectedPost.noAds && (
                <div className="hidden md:block md:col-span-2">
                  {/* One 160px-wide skyscraper: the column is ~176px, so the old
                      padded cards (142px) could not fit any standard unit. */}
                  <div className="sticky top-24">
                    <GoogleAd adFormat="vertical" style={{ display: 'block', width: '160px', minHeight: '600px' }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-white text-slate-900">
          {/* Blog Header */}
          <div className="bg-slate-50 border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="text-center">
                <p className="text-[11px] font-semibold text-navy-500 uppercase tracking-widest mb-4">Educational Research</p>
                <h1 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                  IndianStockMarketGPT <span className="text-navy-500">Blog</span>
                </h1>
                <p className="text-sm md:text-base text-slate-500 max-w-3xl mx-auto leading-relaxed">
                  Educational content to help you master stock market investing with confidence and informed decision-making.
                </p>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="bg-white border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-navy-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Featured Post */}
          {featuredPost && selectedCategory === 'All' && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Award className="w-5 h-5 text-navy-500" />
                  <span className="text-navy-500 font-medium text-sm">Featured Article</span>
                </div>
              </div>
              <Link
                to={`/blog/${featuredPost.slug}`}
                className="block bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-navy-300 hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="md:flex">
                  <div className="md:w-1/2">
                    <OptimizedImage
                      src={featuredPost.image || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&h=400&q=80'}
                      alt={featuredPost.title}
                      className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      width={600}
                      height={400}
                      aspectRatio={1.5}
                      fallbackSrc="/favicon.svg"
                    />
                  </div>
                  <div className="md:w-1/2 p-8 md:p-12">
                    <span className="inline-block bg-navy-50 text-navy-600 border border-navy-100 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      {featuredPost.category}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-navy-500 transition-colors font-serif text-slate-900">
                      {featuredPost.title}
                    </h2>
                    <p className="text-slate-600 mb-6 leading-relaxed text-sm">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <img
                          src={getAuthorAvatar(featuredPost.author).src}
                          alt={featuredPost.author.name}
                          className="w-10 h-10 rounded-full"
                          onError={e => {
                            const fallback = getAuthorAvatar(featuredPost.author).fallback;
                            if (fallback && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                          }}
                        />
                        <div>
                          <div className="font-medium text-slate-900 text-sm">{featuredPost.author.name}</div>
                          <div className="text-xs text-slate-500">
                            {new Date(featuredPost.publishedAt).toLocaleDateString()} • {featuredPost.readTime} min read
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Blog Posts Grid */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post: BlogPost) => (
                <article
                  key={post.id}
                  className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-navy-300 hover:shadow-md transition-all cursor-pointer group"
                >
                  <Link to={`/blog/${post.slug}`} className="block h-full">
                  <div className="relative">
                    <OptimizedImage
                      src={post.image || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&h=400&q=80'}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      width={400}
                      height={192}
                      aspectRatio={2.08}
                      fallbackSrc="/favicon.svg"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm text-slate-700 px-3 py-1 rounded-full text-xs font-medium border border-slate-200">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold mb-3 group-hover:text-navy-500 transition-colors line-clamp-2 text-slate-900 font-serif">
                      {post.title}
                    </h3>
                    <p className="text-slate-500 mb-4 line-clamp-3 leading-relaxed text-sm">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <img
                          src={getAuthorAvatar(post.author).src}
                          alt={post.author.name}
                          className="w-7 h-7 rounded-full"
                          onError={e => {
                            const fallback = getAuthorAvatar(post.author).fallback;
                            if (fallback && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                          }}
                        />
                        <div>
                          <div className="text-xs font-medium text-slate-700">{post.author.name}</div>
                          <div className="text-xs text-slate-500">{new Date(post.publishedAt).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 text-slate-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-xs">{post.readTime} min</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex flex-wrap gap-1">
                        {post.tags.slice(0, 2).map((tag: string, index: number) => (
                          <span
                            key={index}
                            className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded border border-slate-200"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="bg-slate-50 border-t border-slate-100">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
              <div className="bg-navy-50 border border-navy-100 p-3 rounded-xl inline-block mb-6">
                <Zap className="w-8 h-8 text-navy-500" />
              </div>
              <h2 className="text-xl font-bold mb-4 text-slate-900 font-serif">Stay Ahead of the Market</h2>
              <p className="text-sm text-slate-500 mb-8 max-w-2xl mx-auto">
                Analyse any Indian stock using AI — technicals, fundamentals, and sector context in under 90 seconds.
              </p>
              <a
                href="/ask"
                className="inline-flex items-center gap-2 bg-navy-500 hover:bg-navy-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors text-sm"
              >
                Analyse a Stock Free →
              </a>
              <p className="text-xs text-slate-500 mt-4">
                No sign-up required · NSE & BSE stocks
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogPage;
